if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onViewportChange = this.onViewportChange.bind(this);
        this.onSubmitStateMutation = this.onSubmitStateMutation.bind(this);
      }

      connectedCallback() {
        try {
          this.panel = this.querySelector('.sticky-atc');
          this.titleElement = this.querySelector('.sticky-atc__title');
          this.variantElement = this.querySelector('.sticky-atc__variant-name');
          this.priceElement = this.querySelector('.sticky-atc__price');
          this.button = this.querySelector('.sticky-atc__button');
          this.productInfo = this.closest('product-info');
          this.sectionId = this.dataset.sectionId;

          if (!this.panel || !this.button || !this.productInfo || !this.sectionId) return;

          this.button.addEventListener('click', this.onButtonClick);
          this.realButton = this.productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          this.panel.hidden = false;
          this.updatePosition();
          this.bindObserver();
          this.syncFromDom();
          this.variantUnsubscriber =
            typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
              ? subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange)
              : null;
          this.productInfo.addEventListener('variant:changed', this.onVariantChange);
          window.addEventListener('resize', this.onViewportChange);
          this.observeSubmitState();
        } catch (error) {
          console.error(error);
        }
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onButtonClick);
        this.variantUnsubscriber?.();
        this.productInfo?.removeEventListener('variant:changed', this.onVariantChange);
        window.removeEventListener('resize', this.onViewportChange);
        this.intersectionObserver?.disconnect();
        this.buttonObserver?.disconnect();
      }

      bindObserver() {
        if (!this.realButton || typeof IntersectionObserver !== 'function') return;
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            const visible = !entry?.isIntersecting;
            this.toggleVisibility(visible);
          },
          { threshold: 0.4 }
        );
        this.intersectionObserver.observe(this.realButton);
      }

      observeSubmitState() {
        if (!this.realButton || typeof MutationObserver !== 'function') return;
        this.buttonObserver?.disconnect();
        this.buttonObserver = new MutationObserver(this.onSubmitStateMutation);
        this.buttonObserver.observe(this.realButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

      onSubmitStateMutation() {
        this.syncButtonState();
      }

      onViewportChange() {
        this.updatePosition();
      }

      updatePosition() {
        this.dataset.position = window.matchMedia('(min-width: 750px)').matches ? 'desktop' : 'mobile';
      }

      onButtonClick() {
        if (!this.realButton || this.realButton.disabled || this.realButton.getAttribute('aria-disabled') === 'true') {
          return;
        }
        this.realButton.click();
      }

      onVariantChange(event) {
        const detail = event?.detail || event?.data || event;
        const variant = detail?.data?.variant || detail?.variant;
        if (detail?.data?.sectionId && detail.data.sectionId !== this.sectionId) return;
        this.syncFromVariant(variant);
      }

      syncFromDom() {
        const variantJson = this.productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
        let variant = null;
        if (variantJson) {
          try {
            variant = JSON.parse(variantJson);
          } catch (error) {
            console.error(error);
          }
        }
        if (variant) {
          this.syncFromVariant(variant);
          return;
        }
        // Fallback for single / default-variant products: read state directly from the ATC button
        if (this.realButton) {
          const isAvailable =
            !this.realButton.disabled &&
            this.realButton.getAttribute('aria-disabled') !== 'true' &&
            !this.realButton.hasAttribute('data-sold-out');
          this.syncButtonState(isAvailable);
          this.titleElement.textContent = this.dataset.productTitle || '';
          this.variantElement.textContent = '';
          // Read displayed price from the page price element
          const priceEl = this.productInfo.querySelector('.price-item--regular, .price-item--sale.price-item--last');
          if (priceEl) this.priceElement.textContent = priceEl.textContent.trim();
        }
      }

      syncFromVariant(variant) {
        if (!this.panel) return;

        this.titleElement.textContent = this.dataset.productTitle || '';
        const variantTitle =
          variant?.title && variant.title !== 'Default Title' ? variant.title : '';
        this.variantElement.textContent = variantTitle;
        this.variantElement.closest('.sticky-atc__variant').classList.toggle('sticky-atc__variant--single', !variantTitle);
        this.priceElement.textContent = this.formatMoney(variant?.price);
        this.syncButtonState(Boolean(variant?.available));
      }

      syncButtonState(forceAvailable) {
        if (!this.realButton || !this.button) return;
        const isAvailable =
          typeof forceAvailable === 'boolean'
            ? forceAvailable
            : !this.realButton.hasAttribute('disabled') && this.realButton.getAttribute('aria-disabled') !== 'true';
        this.button.disabled = !isAvailable;
        this.button.textContent = isAvailable ? this.dataset.buttonLabel : this.dataset.soldOutLabel;
      }

      toggleVisibility(visible) {
        this.dataset.visible = String(visible);
      }

      formatMoney(cents) {
        if (typeof cents !== 'number') {
          return this.priceElement?.textContent || '';
        }
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency: this.dataset.currencyCode || 'EUR',
        }).format(cents / 100);
      }
    }
  );
}
